import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  Edit,
  UserPlus,
  UserMinus,
  Plus,
  FileText,
  Trash2,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const changeBadgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
  {
    variants: {
      variant: {
        // Group change types
        create: 'bg-card border-border text-foreground',
        delete: 'bg-card border-border text-foreground',
        nameChange: 'bg-card border-border text-foreground',
        addMembers: 'bg-card border-border text-foreground',
        removeMembers: 'bg-card border-border text-foreground',
        // Policy change types
        newPolicies: 'bg-card border-border text-foreground',
        modifiedPolicies: 'bg-card border-border text-foreground',
        removedPolicies: 'bg-card border-border text-foreground',
      },
    },
    defaultVariants: {
      variant: 'nameChange',
    },
  }
);

interface ChangeBadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof changeBadgeVariants> {
  changeType:
    | 'create'
    | 'delete'
    | 'nameChange'
    | 'addMembers'
    | 'removeMembers'
    | 'newPolicies'
    | 'modifiedPolicies'
    | 'removedPolicies';
  text: string;
}

const getIcon = (changeType: ChangeBadgeProps['changeType']) => {
  switch (changeType) {
    case 'create':
      return <Plus className="h-3 w-3" />;
    case 'delete':
      return <Trash2 className="h-3 w-3" />;
    case 'nameChange':
      return <Edit className="h-3 w-3" />;
    case 'addMembers':
      return <UserPlus className="h-3 w-3" />;
    case 'removeMembers':
      return <UserMinus className="h-3 w-3" />;
    case 'newPolicies':
      return <Plus className="h-3 w-3" />;
    case 'modifiedPolicies':
      return <FileText className="h-3 w-3" />;
    case 'removedPolicies':
      return <Trash2 className="h-3 w-3" />;
    default:
      return null;
  }
};

function ChangeBadge({
  className,
  changeType,
  text,
  ...props
}: ChangeBadgeProps) {
  return (
    <div
      className={cn(changeBadgeVariants({ variant: changeType }), className)}
      {...props}
    >
      {getIcon(changeType)}
      <span>{text}</span>
    </div>
  );
}

export { ChangeBadge };
